import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, KeyRound, Download, Trash2, Search, Users, Mail,
  LifeBuoy, LayoutDashboard, Loader2, Check, X, ExternalLink,
  TrendingUp, ImagePlus, Gift, Send,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminStats, adminWaitlist, adminWaitlistCsvUrl, adminWaitlistDelete,
  adminUsers, adminRefunds, adminRefundApprove, adminRefundDeny,
  adminGrantFreeMonth, adminDigestPreview,
} from "@/lib/api";

const KEY_STORAGE = "reviseai_admin_key";
const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "waitlist", label: "Waitlist", icon: Mail },
  { key: "users", label: "Users", icon: Users },
  { key: "refunds", label: "Refunds", icon: LifeBuoy },
];

/** Normalise FastAPI/axios error payloads, pydantic 422 returns an array of
 * {msg,...} objects which crashes React if passed straight to <toast>. */
function errMsg(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d[0]?.msg || fallback;
  if (typeof d === "string") return d;
  return fallback;
}

function fmtDate(iso) {
  if (!iso) return ",";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function fmtMoney(amount, currency) {
  if (typeof amount !== "number") return ",";
  return `${(amount / 100).toFixed(2)} ${(currency || "gbp").toUpperCase()}`;
}

function StatusBadge({ status }) {
  const colors = {
    active: "bg-[#9DFFB0] dark:bg-[#68D391] text-black",
    trialing: "bg-[#90E0FF] dark:bg-[#63B3ED] text-black",
    past_due: "bg-[#FFB38E] dark:bg-[#F6AD55] text-black",
    canceled: "bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300",
    pending: "bg-[#FFE066] dark:bg-[#F6E05E] text-black",
    approved: "bg-[#9DFFB0] dark:bg-[#68D391] text-black",
    denied: "bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300",
  };
  if (!status) return <span className="text-zinc-400">,</span>;
  return (
    <span className={`nb-border rounded-full px-2 py-0.5 text-[11px] font-black uppercase ${colors[status] || "bg-zinc-100 text-zinc-700"}`}>
      {status}
    </span>
  );
}

export default function AdminConsole() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [keyDraft, setKeyDraft] = useState("");
  const [tab, setTab] = useState("overview");

  const saveKey = (e) => {
    e.preventDefault();
    const k = keyDraft.trim();
    if (!k) return toast.error("Enter the admin key");
    sessionStorage.setItem(KEY_STORAGE, k);
    setKey(k);
    setKeyDraft("");
  };

  if (!key) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 flex items-center justify-center p-6">
        <form onSubmit={saveKey} className="nb-card p-8 w-full max-w-md space-y-4" data-testid="console-key-form">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" strokeWidth={2.5} />
            <h1 className="font-display text-2xl font-black">Admin console</h1>
          </div>
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
            Paste the admin key (RECONCILE_KEY). Stays only in this browser tab.
          </p>
          <input
            type="password"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder="rcl_..."
            autoFocus
            data-testid="console-key-input"
            className="nb-input"
          />
          <button type="submit" className="nb-button px-4 py-3 w-full bg-[#D4C4FB] dark:bg-[#B794F4] text-black" data-testid="console-key-submit">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="admin-console-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            data-testid="console-exit"
            className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow"
          >
            <ArrowLeft className="w-4 h-4" /> Exit
          </button>
          <div className="font-display font-extrabold text-xl flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5" /> Admin console
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/diagrams")}
              className="nb-border rounded-xl px-3 py-2 text-xs font-bold hover:nb-shadow flex items-center gap-1"
              data-testid="console-nav-diagrams"
              title="Verified Diagrams"
            >
              <ImagePlus className="w-3.5 h-3.5" /> Diagrams
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem(KEY_STORAGE);
                setKey("");
              }}
              className="nb-border rounded-xl px-3 py-2 text-xs font-bold hover:nb-shadow"
              data-testid="console-lock"
            >
              Lock
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 pb-2 flex gap-1 overflow-x-auto" data-testid="console-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`console-tab-${t.key}`}
                className={`nb-border rounded-t-xl rounded-b-none px-4 py-2 text-sm font-bold flex items-center gap-2 whitespace-nowrap ${
                  active ? "bg-[#D4C4FB] dark:bg-[#B794F4] text-black" : "bg-white dark:bg-zinc-900 hover:nb-shadow"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="max-w-6xl mx-auto p-6 sm:p-8">
        {tab === "overview" && <OverviewTab adminKey={key} />}
        {tab === "waitlist" && <WaitlistTab adminKey={key} />}
        {tab === "users" && <UsersTab adminKey={key} />}
        {tab === "refunds" && <RefundsTab adminKey={key} />}
      </div>
    </div>
  );
}

// ============================================================
// Overview
// ============================================================
function OverviewTab({ adminKey }) {
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    adminStats(adminKey).then(setStats).catch(() => setStats(null));
  }, [adminKey]);

  const sendPreview = async () => {
    setSending(true);
    try {
      const r = await adminDigestPreview(adminKey);
      toast.success(r.sent ? "Preview digest sent to ADMIN_EMAIL" : "Built but email provider not configured");
    } catch (e) {
      toast.error(errMsg(e, "Preview failed"));
    } finally {
      setSending(false);
    }
  };

  if (!stats) return <div className="flex items-center gap-2 font-bold text-zinc-500"><Loader2 className="w-4 h-4 animate-spin"/>Loading stats…</div>;
  const cards = [
    { label: "Waitlist", value: stats.waitlist.total, sub: `${stats.waitlist.last_7_days} new this week`, color: "bg-[#FFE066] dark:bg-[#F6E05E]" },
    { label: "Active subscribers", value: stats.users.active, sub: `${stats.users.trialing} trialing`, color: "bg-[#9DFFB0] dark:bg-[#68D391]" },
    { label: "Past due", value: stats.users.past_due, sub: `${stats.users.canceled} canceled`, color: "bg-[#FFB38E] dark:bg-[#F6AD55]" },
    { label: "Pending refunds", value: stats.refunds.pending, sub: "Review in Refunds tab", color: "bg-[#D4C4FB] dark:bg-[#B794F4]" },
    { label: "Children profiles", value: stats.children.total, sub: `${stats.users.total} parents total`, color: "bg-[#90E0FF] dark:bg-[#63B3ED]" },
    { label: "Billing", value: stats.billing_enabled ? "ON" : "OFF", sub: stats.billing_enabled ? "Stripe live" : "No STRIPE_SECRET_KEY", color: stats.billing_enabled ? "bg-[#9DFFB0]" : "bg-zinc-200" },
  ];
  return (
    <div className="space-y-6" data-testid="overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="overview-grid">
        {cards.map((c) => (
          <div key={c.label} className={`nb-card p-5 ${c.color} text-black`}>
            <div className="label-caps opacity-80">{c.label}</div>
            <div className="font-display text-4xl font-black mt-2 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" strokeWidth={2.5} /> {c.value}
            </div>
            <div className="text-xs font-bold opacity-80 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="nb-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="label-caps">Weekly digest</div>
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 mt-1">
            Monday 9am UTC, auto-sent to ADMIN_EMAIL. Send a preview now to check what it looks like.
          </p>
        </div>
        <button
          onClick={sendPreview}
          disabled={sending}
          data-testid="digest-preview-btn"
          className="nb-button px-4 py-2.5 flex items-center gap-2 bg-[#FFE066] dark:bg-[#F6E05E] text-black disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send preview"}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Waitlist
// ============================================================
function WaitlistTab({ adminKey }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");

  const reload = async () => {
    setRows(null);
    try {
      const r = await adminWaitlist(adminKey, { q: q || undefined, role: role || undefined });
      setRows(r);
    } catch (e) {
      toast.error("Failed to load waitlist");
      setRows([]);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [role]);

  const remove = async (id) => {
    if (!window.confirm("Remove this signup?")) return;
    try {
      await adminWaitlistDelete(id, adminKey);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="waitlist-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Search email…"
            className="nb-input pl-9"
            data-testid="waitlist-search"
          />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="nb-input" data-testid="waitlist-role-filter">
          <option value="">All roles</option>
          <option value="parent">Parent</option>
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
        </select>
        <button onClick={reload} className="nb-button px-4 py-2" data-testid="waitlist-apply">Apply</button>
        <a
          href={adminWaitlistCsvUrl(adminKey)}
          target="_blank"
          rel="noreferrer"
          className="nb-border rounded-xl px-3 py-2 text-sm font-bold flex items-center gap-1 hover:nb-shadow bg-white dark:bg-zinc-900"
          data-testid="waitlist-csv"
        >
          <Download className="w-4 h-4" /> CSV
        </a>
      </div>
      <TableWrap testId="waitlist-table" rows={rows} empty="No signups yet.">
        <thead className="text-xs font-black uppercase bg-zinc-50 dark:bg-zinc-800">
          <tr>
            <th className="text-left p-3">Email</th>
            <th className="text-left p-3">Role</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Joined</th>
            <th className="text-left p-3">Email sent</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.id} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`waitlist-row-${r.id}`}>
              <td className="p-3 font-mono text-xs break-all">{r.email}</td>
              <td className="p-3"><StatusBadge status={r.role} /></td>
              <td className="p-3 text-sm font-medium">{r.first_name || ","}</td>
              <td className="p-3 text-sm">{fmtDate(r.created_at)}</td>
              <td className="p-3">{r.email_sent ? <Check className="w-4 h-4 text-green-600"/> : <X className="w-4 h-4 text-zinc-400"/>}</td>
              <td className="p-3 text-right">
                <button onClick={() => remove(r.id)} className="nb-border rounded-lg p-1.5 hover:nb-shadow" data-testid={`waitlist-delete-${r.id}`} title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </div>
  );
}

// ============================================================
// Users
// ============================================================
function UsersTab({ adminKey }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [granting, setGranting] = useState(null); // { user_id, email, days, note }

  const reload = async () => {
    setRows(null);
    try {
      const r = await adminUsers(adminKey, { q: q || undefined, status: status || undefined });
      setRows(r);
    } catch { setRows([]); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  const submitGrant = async () => {
    if (!granting) return;
    try {
      const res = await adminGrantFreeMonth(granting.user_id, adminKey, {
        days: Number(granting.days) || 30,
        note: granting.note || "",
      });
      toast.success(`Granted ${res.grant.days} free days · next bill ${new Date(res.new_trial_end).toLocaleDateString("en-GB")}`);
      setGranting(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Grant failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload()} placeholder="Search email…" className="nb-input pl-9" data-testid="users-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="nb-input" data-testid="users-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="canceled">Canceled</option>
        </select>
        <button onClick={reload} className="nb-button px-4 py-2" data-testid="users-apply">Apply</button>
      </div>
      <TableWrap testId="users-table" rows={rows} empty="No users match.">
        <thead className="text-xs font-black uppercase bg-zinc-50 dark:bg-zinc-800">
          <tr>
            <th className="text-left p-3">Email</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Plan</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Kids</th>
            <th className="text-left p-3">Joined</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.user_id} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`users-row-${r.user_id}`}>
              <td className="p-3 font-mono text-xs break-all">{r.email}</td>
              <td className="p-3 text-sm font-medium">{r.name || ","}</td>
              <td className="p-3 text-sm">{r.subscription_plan || ","}</td>
              <td className="p-3"><StatusBadge status={r.subscription_status} /></td>
              <td className="p-3 text-sm font-bold">{r.children_count ?? 0}</td>
              <td className="p-3 text-sm">{fmtDate(r.created_at)}</td>
              <td className="p-3 text-right whitespace-nowrap">
                <div className="flex gap-1 justify-end items-center">
                  {r.stripe_subscription_id && r.subscription_status && !["canceled", "incomplete_expired"].includes(r.subscription_status) && (
                    <button
                      onClick={() => setGranting({ user_id: r.user_id, email: r.email, days: 30, note: "" })}
                      className="nb-border rounded-lg px-2 py-1 text-xs font-bold flex items-center gap-1 bg-[#FFE066] dark:bg-[#F6E05E] text-black hover:nb-shadow"
                      data-testid={`users-grant-${r.user_id}`}
                      title="Grant free days"
                    >
                      <Gift className="w-3 h-3" /> Free month
                    </button>
                  )}
                  {r.stripe_customer_id && (
                    <a
                      href={`https://dashboard.stripe.com/customers/${r.stripe_customer_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="nb-border rounded-lg p-1.5 hover:nb-shadow inline-flex"
                      data-testid={`users-stripe-${r.user_id}`}
                      title="Open in Stripe"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      {granting && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setGranting(null)}>
          <div className="nb-card bg-white dark:bg-zinc-900 w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="grant-modal">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5" strokeWidth={2.5} />
              <h3 className="font-display text-2xl font-black">Grant free days</h3>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 font-medium">
              Pushes this parent's next billing date out via Stripe's trial_end, no charge during the extension. Works on monthly or annual plans.
            </p>
            <div className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 nb-border rounded-lg p-2 break-all">{granting.email}</div>
            <label className="block">
              <div className="label-caps mb-1">Days</div>
              <input
                type="number"
                min="1"
                max="365"
                value={granting.days}
                onChange={(e) => setGranting((v) => ({ ...v, days: e.target.value }))}
                data-testid="grant-days-input"
                className="nb-input"
              />
            </label>
            <label className="block">
              <div className="label-caps mb-1">Internal note <span className="normal-case text-zinc-400">(not emailed)</span></div>
              <textarea
                value={granting.note}
                onChange={(e) => setGranting((v) => ({ ...v, note: e.target.value }))}
                placeholder="Why, kept for audit log"
                rows={2}
                maxLength={300}
                data-testid="grant-note"
                className="nb-input"
              />
            </label>
            <div className="flex gap-2">
              <button onClick={() => setGranting(null)} className="nb-border rounded-xl px-4 py-2 font-bold text-sm flex-1 hover:nb-shadow" data-testid="grant-cancel">Cancel</button>
              <button
                onClick={submitGrant}
                className="nb-button px-4 py-2 flex-1 bg-[#9DFFB0] dark:bg-[#68D391] text-black"
                data-testid="grant-confirm"
              >
                Grant {granting.days || 30} days
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Refunds
// ============================================================
function RefundsTab({ adminKey }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("pending");
  const [resolving, setResolving] = useState(null); // { id, mode: "approve"|"deny", note, issue }
  const reload = async () => {
    setRows(null);
    try { setRows(await adminRefunds(adminKey, status || undefined)); } catch { setRows([]); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  const submit = async () => {
    if (!resolving) return;
    try {
      const body = { note: resolving.note || "", issue_stripe_refund: resolving.issue !== false };
      if (resolving.mode === "approve") await adminRefundApprove(resolving.id, adminKey, body);
      else await adminRefundDeny(resolving.id, adminKey, body);
      toast.success(resolving.mode === "approve" ? "Refund approved + emailed" : "Request denied + emailed");
      setResolving(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="refunds-tab">
      <div className="flex gap-2">
        {["pending", "approved", "denied", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            data-testid={`refunds-filter-${s || "all"}`}
            className={`nb-border rounded-xl px-3 py-2 text-sm font-bold ${status === s ? "bg-[#D4C4FB] dark:bg-[#B794F4] text-black" : "bg-white dark:bg-zinc-900 hover:nb-shadow"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>
      <TableWrap testId="refunds-table" rows={rows} empty="No requests match.">
        <thead className="text-xs font-black uppercase bg-zinc-50 dark:bg-zinc-800">
          <tr>
            <th className="text-left p-3">Email</th>
            <th className="text-left p-3">Reason</th>
            <th className="text-left p-3">Plan</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Submitted</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.id} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`refunds-row-${r.id}`}>
              <td className="p-3">
                <div className="font-mono text-xs break-all">{r.email}</div>
                {r.details && <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2 max-w-xs">{r.details}</div>}
              </td>
              <td className="p-3 text-sm font-medium">{r.reason_label || r.reason}</td>
              <td className="p-3 text-xs">{r.subscription_plan || ","}</td>
              <td className="p-3"><StatusBadge status={r.status} /></td>
              <td className="p-3 text-sm">{fmtDate(r.created_at)}</td>
              <td className="p-3 text-right whitespace-nowrap">
                {r.status === "pending" ? (
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => setResolving({ id: r.id, mode: "approve", note: "", issue: true, email: r.email })}
                      className="nb-border rounded-lg px-2 py-1 text-xs font-bold bg-[#9DFFB0] dark:bg-[#68D391] text-black hover:nb-shadow"
                      data-testid={`refunds-approve-${r.id}`}
                    >
                      <Check className="w-3 h-3 inline" /> Approve
                    </button>
                    <button
                      onClick={() => setResolving({ id: r.id, mode: "deny", note: "", email: r.email })}
                      className="nb-border rounded-lg px-2 py-1 text-xs font-bold hover:nb-shadow"
                      data-testid={`refunds-deny-${r.id}`}
                    >
                      <X className="w-3 h-3 inline" /> Deny
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-zinc-500 font-medium">
                    {r.stripe_refund?.refund_id ? fmtMoney(r.stripe_refund.amount, r.stripe_refund.currency) : ","}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      {resolving && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setResolving(null)}>
          <div className="nb-card bg-white dark:bg-zinc-900 w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="refunds-modal">
            <h3 className="font-display text-2xl font-black">
              {resolving.mode === "approve" ? "Approve refund" : "Deny request"}
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 font-medium">
              {resolving.mode === "approve"
                ? "Refund the most recent successful payment on this customer in Stripe. Parent will get an approval email."
                : "Mark the request as denied. Parent will get an email with your note."}
            </p>
            <div className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 nb-border rounded-lg p-2 break-all">{resolving.email}</div>
            {resolving.mode === "approve" && (
              <label className="flex items-center gap-2 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={resolving.issue !== false}
                  onChange={(e) => setResolving((v) => ({ ...v, issue: e.target.checked }))}
                  data-testid="refunds-issue-checkbox"
                />
                Issue the refund via Stripe now
              </label>
            )}
            <textarea
              value={resolving.note}
              onChange={(e) => setResolving((v) => ({ ...v, note: e.target.value }))}
              placeholder={resolving.mode === "deny" ? "Reason for the parent (included in the email)…" : "Internal note (optional)"}
              rows={3}
              maxLength={500}
              className="nb-input"
              data-testid="refunds-note"
            />
            <div className="flex gap-2">
              <button onClick={() => setResolving(null)} className="nb-border rounded-xl px-4 py-2 font-bold text-sm flex-1 hover:nb-shadow" data-testid="refunds-cancel">Cancel</button>
              <button
                onClick={submit}
                className={`nb-button px-4 py-2 flex-1 text-black ${resolving.mode === "approve" ? "bg-[#9DFFB0] dark:bg-[#68D391]" : "bg-[#FFB38E] dark:bg-[#F6AD55]"}`}
                data-testid="refunds-confirm"
              >
                {resolving.mode === "approve" ? "Approve" : "Deny"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Shared table wrapper
// ============================================================
function TableWrap({ children, rows, empty, testId }) {
  if (rows === null) return <div className="flex items-center gap-2 font-bold text-zinc-500"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>;
  if (rows.length === 0) return <div className="nb-card p-8 text-center text-sm font-bold text-zinc-500" data-testid={`${testId}-empty`}>{empty}</div>;
  return (
    <div className="nb-card overflow-x-auto">
      <table className="w-full" data-testid={testId}>
        {children}
      </table>
    </div>
  );
}
